import type { SearchResult } from '@/services/auth/types';
import { ORDERED_SOURCE_KEYS, type ActiveSources } from './source-bookkeeping';

/** Customer result with a client-side relevance score computed via cmdk's commandScore. */
export type ScoredResult = SearchResult & { clientScore: number };

export type ResultsMap = {
  [K in keyof ActiveSources]: K extends 'people' ? ScoredResult[] : SearchResult[];
};

const MULTI_RESULT_POSITIONAL_DECAY_TOP_SCORE = 0.99;

/**
 * Merges Vertex search results and scored customer results into a single ranked list.
 *
 * Vertex results use relevance_score when present; fall back to positional decay
 * for results where Vertex does not return a score. A single fallback result keeps
 * score 1.0; multi-result fallbacks start at 0.99 so exact-match customers can rank first.
 * Customer results use their pre-computed clientScore.
 *
 * Stable sort descending — Vertex items are listed first in the combined array so they
 * win on equal scores, preserving Vertex's implicit ranking within each bucket.
 */
export function mergeByScoredInterleave(vertexResults: SearchResult[], peopleResults: ScoredResult[]): SearchResult[] {
  const total = vertexResults.length;

  const scored = [
    ...vertexResults.map((result, index) => ({
      result,
      score: result.relevance_score ?? getPositionalDecayScore(index, total),
    })),
    ...peopleResults.map((result) => ({
      result,
      score: result.clientScore,
    })),
  ];

  scored.sort((a, b) => b.score - a.score);

  return scored.map(({ result }) => result);
}

function getPositionalDecayScore(index: number, total: number): number {
  if (total <= 0) return 0;
  if (total === 1) return 1;
  return Math.max(0, MULTI_RESULT_POSITIONAL_DECAY_TOP_SCORE - index / total);
}

function collectPeopleAndSearch(
  activeSources: Pick<ActiveSources, 'search' | 'people'>,
  results: Pick<ResultsMap, 'search' | 'people'>,
): SearchResult[] {
  if (activeSources.search && activeSources.people) {
    return mergeByScoredInterleave(results.search, results.people);
  }
  if (activeSources.search) return [...results.search];
  if (activeSources.people) return [...results.people];
  return [];
}

/** Assembles all active API results into a single ordered list. */
export function buildAllApiResults(activeSources: ActiveSources, results: ResultsMap): SearchResult[] {
  const output = collectPeopleAndSearch(activeSources, results);
  for (const key of ORDERED_SOURCE_KEYS) {
    if (activeSources[key]) output.push(...results[key]);
  }
  return output;
}
